<template lang="pug">
.rooms-schedules-tab
  .mb-3
  .d-flex.justify-content-start.align-items-center.mb-2
    .d-flex.justify-content-start
      .exist-empty-room-box.mr-2
      .exist-empty-room-box-label.mr-2 空床あり
      .empty-room-box.mr-2
      .empty-room-box-label.mr-2 空室状態
      .over-capacity-room-box.mr-2
      .over-capacity-room-box-label.mr-2 部屋定員超過
    .d-flex.justify-content-end.flex-fill
      button.btn.move-month-btn.mr-2(type="button" @click="onPreviousMonth") ＜ 前の月
      button.btn.move-month-btn.mr-2(type="button" @click="onCurrentMonth") 今月
      button.btn.move-month-btn(type="button" @click="onNextMonth") 次の月 ＞
  RoomsSchedulesTable(
    v-model="items"
    :filter="filter"
    :filterFields="filterFields"
    :fields="fields"
    :isLoading="isLoading"
    :useType="useType"
  )
  .table-options.d-flex.flex-row.justify-content-end.align-items-center
    //- CSVダウンロード
    button.btn.download-btn.mr-2(:class="{ 'disable': isLoading }" :disabled="isLoading" @click="onCsvDownload") ダウンロード
    //- フィルタリング
    span.table-option-text 利用者グループ:
    .filter
      b-form-select(
        id="filter-input"
        v-model="filter"
        :options="filterOptions"
        size="md"
      )
</template>
<script lang="ts">
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore } from "@/store/store";

import { nationalHolidaysStore } from "@/store/store";
import { NationalHolidaysGetModel } from "@/commons/services/NationalHolidays";

import RoomsSchedulesService, { RoomSchedulesGetModel } from "@/commons/services/RoomsSchedules";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import RoomsSchedulesTable, { RoomsSchedulesTableField, RoomsSchedulesTableItem } from "@/pc/containers/roomSchedules/RoomsSchedulesTable.vue";
import { RoomType, UseType } from "@/commons/services/Rooms";

/**
 * ### 部屋スケジュールタブコンポーネント
 */
@Component({
  components: {
    RoomsSchedulesTable
  }
})
export default class RoomsSchedulesTab extends Vue {
  /** 施設サービス */
  @Inject("RoomsSchedulesService") roomsSchedulesService!: RoomsSchedulesService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 用途 */
  @Prop()
  useType!: UseType;

  /** 祝日MAP */
  holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();

  /** 表示対象年 */
  year: number = new Date().getFullYear();
  /** 表示対象月 */
  month: number = new Date().getMonth() + 1;

  /** テーブル列名 */
  fields: Array<RoomsSchedulesTableField> = [];
  /** テーブル各行の情報 */
  items: Array<RoomsSchedulesTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;

  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["residentGroupId"];

  /**
   * ### 画面情報を再作成する
   */
  public async refresh(): Promise<void> {
    this.createView();

    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  async mounted(): Promise<void> {
    this.holidaysMap = this.createHolidaysMap(nationalHolidaysStore.holidays);
    await this.createView();
    return;
  }

  private createHolidaysMap(holidays: Array<NationalHolidaysGetModel>): Map<string, NationalHolidaysGetModel> {
    const holidaysMap: Map<string, NationalHolidaysGetModel> = new Map();
    for (const holiday of holidays) {
      holidaysMap.set(holiday.date, holiday);
    }
    return holidaysMap;
  }

  /**
   * ### 画面情報作成
   *
   * - 空床情報を取得し、table用の行情報とする
   */
  private async createView(): Promise<void> {
    this.isLoading = true;

    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<RoomsSchedulesTableField> = this.createFields();
    this.fields = fields;

    // グループIDとグループ名の変換用にグループ情報のMAPを作る
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    const roomsSchedules: Array<RoomSchedulesGetModel> = await this.getRoomsSchedules(this.useType, this.year as number, this.month as number);
    const items: Array<RoomsSchedulesTableItem> = this.createItems(roomsSchedules);
    this.items = items;

    this.isLoading = false;

    return;
  }

  /**
   * ### テーブル列名を作成する
   *
   * - 日付の列は月毎に可変なので、当月の最終日分のfieldを作成する
   */
  private createFields(): Array<RoomsSchedulesTableField> {
    let fields: Array<RoomsSchedulesTableField> = [];

    fields.push({ key: "name", label: "部屋名", sortable: false, sortDirection: "desc", class: "room-name" });
    fields.push({ key: "type", label: "種別（人数）", sortable: false, sortDirection: "desc", class: "room-type" });

    // 当月の最終日を取得する。「来月の0日」を指定すると「当月の最終日」のDateを作れる
    const lastDay: number = new Date(this.year as number, this.month as number, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
      const week: string = this.getWeekName(this.year, this.month, i);
      const holiday: Date = new Date(`${this.year}/${this.month}/${i}`);
      const holidayName: NationalHolidaysGetModel | undefined = this.holidaysMap.get(this.$dateUtil.convertDateToString(holiday, "yyyy-mm-dd"));
      fields.push({
        key: `day${i}am`,
        label: `午前`,
        sortable: false,
        sortDirection: "desc",
        class: "day",
        datetime: `${this.month}/${i}`,
        week: `${week}`,
        holidayName: holidayName ? holidayName.name : ""
      });
      fields.push({
        key: `day${i}pm`,
        label: `午後`,
        sortable: false,
        sortDirection: "desc",
        class: "day",
        datetime: `${this.month}/${i}`,
        week: `${week}`,
        holidayName: holidayName ? holidayName.name : ""
      });
    }

    return fields;
  }

  /**
   * ### 曜日文字列を取得する
   */
  private getWeekName(year: number, month: number, day: number): string {
    const weekNameMap: Map<number, string> = new Map([
      [0, "日"],
      [1, "月"],
      [2, "火"],
      [3, "水"],
      [4, "木"],
      [5, "金"],
      [6, "土"]
    ]);
    const date: Date = new Date(year, month - 1, day); // 月は0始まりなので、-1する
    const week: string | undefined = weekNameMap.get(date.getDay());

    return week ? week : "";
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  private async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  private createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name + `-${this.useType}`] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name + `-${this.useType}`];
    }
    return;
  }

  /**
   * ### 空床情報を取得する
   *
   * - 用途を指定して表示情報を絞り込む
   */
  private async getRoomsSchedules(useType: UseType, year: number, month: number): Promise<Array<RoomSchedulesGetModel>> {
    const params: any = {
      useType: useType
    };
    const response: Array<RoomSchedulesGetModel> = await this.roomsSchedulesService.gets(userStore, year, month, params);
    return response;
  }

  /**
   * ### テーブル行を作成する
   *
   * - roomsSchedules内のschedulesは対象月の日数*AMPM分の要素を持つ
   * - 要素番号が奇数ならAM、偶数ならPMと判断する
   * - 日付は 要素番号/2 + 1で求められる
   * - 日付にひもづく利用者数が0人＝空室と判断し、BOOTSTRAP-VUEのtableコンポーネントにCSSクラスを付与させる
   *   - ※_cellVariantsにkey名でvariant名を渡すと「table-{variant名}」のCSSクラスが付与される仕様
   * - 日付にひもづく利用者数が<部屋の許容人数の場合、空床ありと判断しBOOTSTRAP-VUEのtableコンポーネントにCSSクラスを付与させる
   */
  private createItems(roomsSchedules: Array<RoomSchedulesGetModel>): Array<RoomsSchedulesTableItem> {
    let items: Array<RoomsSchedulesTableItem> = [];

    for (const room of roomsSchedules) {
      // 部屋用途、部屋タイプが空床管理対象外はスキップする
      if (room.useType == UseType.UNMANAGED || room.roomType == RoomType.UNMANAGED) {
        continue;
      }
      // 部屋用途が渡された部屋用途と異なる場合はスキップする
      if (room.useType != this.useType) {
        continue;
      }

      let _cellVariants: any = {};
      for (let i = 0; i < room.schedules.length; i++) {
        const ampm: string = i % 2 === 0 ? "am" : "pm";
        const day: number = parseInt(`${i / 2 + 1}`, 10);

        if (room.schedules[i].residents.length == 0) {
          _cellVariants[`day${day}${ampm}`] = "empty-room";
        } else if (room.schedules[i].residents.length < room.capacity) {
          _cellVariants[`day${day}${ampm}`] = "exist-empty-room";
        } else if (room.capacity < room.schedules[i].residents.length) {
          _cellVariants[`day${day}${ampm}`] = "over-capacity-room";
        }
      }

      items.push({
        ...room,
        _cellVariants: _cellVariants
      });
    }

    return items;
  }

  /**
   * ### 前の月ボタン押下イベント
   *
   * - 画面表示中の年、月を使用してDateを作成
   * - Dateを前の月に変更する
   * - 変更した年・月を画面表示する
   */
  async onPreviousMonth(): Promise<void> {
    let currentDate: Date = new Date(this.year, this.month - 1, 1); // monthは0始まりなので-1して使用する
    currentDate.setMonth(currentDate.getMonth() - 1);

    this.year = currentDate.getFullYear();
    this.month = currentDate.getMonth() + 1;
    await this.createView();

    return;
  }

  /**
   * ### 今月ボタン押下イベント
   *
   * - 現在のDateを作成
   * - 現在の年・月を画面表示する
   */
  async onCurrentMonth(): Promise<void> {
    let toDay: Date = new Date();

    this.year = toDay.getFullYear();
    this.month = toDay.getMonth() + 1;
    await this.createView();

    return;
  }

  /**
   * ### 次の月押下イベント
   *
   * - 画面表示中の年、月を使用してDateを作成
   * - Dateを次の月に変更する
   * - 変更した年・月を画面表示する
   */
  async onNextMonth(): Promise<void> {
    let currentDate: Date = new Date(this.year, this.month - 1, 1); // monthは0始まりなので-1して使用する
    currentDate.setMonth(currentDate.getMonth() + 1);

    this.year = currentDate.getFullYear();
    this.month = currentDate.getMonth() + 1;
    await this.createView();

    return;
  }

  /**
   * ### 利用者一覧をCSVダウンロードする
   */
  onCsvDownload(): void {
    const csv: string = this.createCsv(this.items);
    const fileName: string = this.createCsvFileName();
    this.downloadCsv(csv, fileName);
    return;
  }

  /**
   * ### 利用者一覧CSVを作成する
   *
   * - 1行目の先頭はexcel等での文字化け対策でBOMを付与する
   * - 1行目は項目名を作る
   * - 2行目以降は行情報にあたる情報を作成して出力する
   */
  private createCsv(items: Array<RoomsSchedulesTableItem>): string {
    const csv: string[] = [];

    // 1行目の列名を作成して追加する。日付情報は計算で求める
    const firstRowNames: string[] = ["部屋名", "種別（人数）"];
    const lastDay: number = new Date(this.year as number, this.month as number, 0).getDate();
    for (let i = 1; i <= lastDay; i++) {
      const week: string = this.getWeekName(this.year, this.month, i);
      firstRowNames.push(`${this.month}/${i}（${week}）`);
      firstRowNames.push(""); // 1行目では日付を列名とし、2行目でその日付の午前、午後を列名とするため空文字を追加
    }
    csv.push(`\ufeff` + `${firstRowNames.join(",")}`);

    // 2行目の列名を作成して追加する。1行目の日付に対して午前、午後を出力する
    const secondRowNames: string[] = ["", ""];
    for (let i = 1; i <= lastDay; i++) {
      secondRowNames.push("午前");
      secondRowNames.push("午後");
    }
    csv.push(`\ufeff` + `${secondRowNames.join(",")}`);

    // 行情報を追加する
    for (const item of items) {
      const line: string[] = [];

      line.push(item.name);
      line.push(`${this.getRoomTypeName(item)}${this.getCapacity(item)}`);
      for (const [index, schedule] of item.schedules.entries()) {
        const residentNames: string[] = [];
        for (const resident of schedule.residents) {
          residentNames.push(resident.residentName);
        }
        line.push(residentNames.join("、"));
      }

      csv.push(`${line.join(",")}`);
    }
    return csv.join("\n");
  }

  /**
   * ### 部屋種別に対応した文字列を返す
   */
  private getRoomTypeName(item: RoomsSchedulesTableItem): string {
    switch (item.roomType) {
      case RoomType.SINGLE: {
        return "個室";
      }
      case RoomType.DOUBLE: {
        return "2人部屋（夫婦部屋）";
      }
      case RoomType.MULTI_MEN: {
        return "多床室（男性）";
      }
      case RoomType.MULTI_WOMEN: {
        return "多床室（女性）";
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### 人数を返す
   *
   * - 多床室以外は人数は固定なので空文字を返す
   * - 多床室の場合は人数を返す
   */
  private getCapacity(item: RoomsSchedulesTableItem): string {
    switch (item.roomType) {
      case RoomType.SINGLE:
      case RoomType.DOUBLE: {
        return "";
      }
      case RoomType.MULTI_MEN:
      case RoomType.MULTI_WOMEN: {
        return `（${item.capacity}人）`;
      }
      default: {
        return "";
      }
    }
  }

  /**
   * ### CSVファイル名を作成する
   */
  private createCsvFileName(): string {
    const date: string = this.$dateUtil.convertDateToString(new Date(), "yyyymmdd");
    const scheduleSuffix: string = this.useType == 1 ? `入居用` : `ショートステイ`;
    const fileName: string = `HitomeQコネクト-空床スケジュール（${scheduleSuffix}）-${date}.csv`;
    return fileName;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  private downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    return;
  }

  /**
   * ### フィルタリング条件の保存
   *
   * - 親コンポーネントから呼び出される
   */
  public setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name + `-${this.useType}`, filter: this.filter });
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.rooms-schedules-tab {
  width: 100%;
  overflow: auto;

  .exist-empty-room-box,
  .empty-room-box,
  .over-capacity-room-box {
    height: 24px;
    width: 120px;
    border: solid 2px $border-darkgray;
  }
  .exist-empty-room-box {
    background-color: $bg-exist-empty-room;
  }
  .empty-room-box {
    background-color: $bg-empty-room;
  }
  .over-capacity-room-box {
    background-color: $bg-over-capacity-room;
  }

  .move-month-btn {
    font-size: 14px !important;
    width: 90px !important;
    height: 30px !important;
  }
}
</style>
